import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import { ClerkProvider, SignedIn, SignedOut, UserButton, RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { QueryProvider } from "./providers/QueryProvider";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Product } from "./pages/Product";
import { NewProduct } from "./pages/NewProduct";
import { Integrations } from "./pages/Integrations";
import "./index.css";

const clerkPubKey = process.env.BUN_PUBLIC_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error("Missing CLERK_PUBLISHABLE_KEY environment variable");
}

const features = [
  {
    title: "Subscription Management",
    description:
      "Create and manage subscription plans with flexible billing cycles. Update pricing, pause accounts, and handle upgrades or downgrades with ease.",
  },
  {
    title: "Churn Prevention",
    description:
      "Identify at-risk subscribers before they cancel. Get insights into engagement patterns and take action to retain customers at the right moment.",
  },
  {
    title: "Revenue Analytics",
    description:
      "Track MRR, ARR, and churn rate in real time. Understand what's driving growth and where you're losing revenue so you can act fast.",
  },
];

function Landing() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="w-full border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Pull</h1>
        <div className="flex items-center gap-3">
          <SignedOut>
            <Link
              to="/login"
              className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/login"
              className="bg-[#F5D020] text-gray-900 text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#d4b31b] transition-colors"
            >
              Get Started
            </Link>
          </SignedOut>
          <SignedIn>
            <UserButton />
          </SignedIn>
        </div>
      </header>

      {/* Hero */}
      <section className="w-full bg-[#F5D020] px-8 py-24 text-center">
        <h2 className="text-5xl font-bold mb-6 leading-tight text-gray-900">
          Stop losing subscribers.<br />Start growing revenue.
        </h2>
        <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-10">
          Pull gives you everything you need to manage subscription services, understand your
          customers, and reduce churn — before it costs you.
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to="/login"
            className="bg-gray-900 text-white font-semibold px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Get Started
          </Link>
          <Link
            to="/login"
            className="border border-gray-900 text-gray-900 font-semibold px-6 py-3 rounded-lg hover:bg-[#d4b31b] transition-colors"
          >
            Sign In
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="w-full px-8 py-20 bg-gray-50">
        <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Everything you need to retain subscribers
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {features.map((f) => (
            <div key={f.title} className="bg-white rounded-xl border border-gray-200 p-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">{f.title}</h4>
              <p className="text-gray-500 leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full px-8 py-20 text-center">
        <h3 className="text-3xl font-bold text-gray-900 mb-4">Ready to reduce churn?</h3>
        <p className="text-lg text-gray-500 mb-8 max-w-xl mx-auto">
          Join vendors already using Pull to keep subscribers happy and revenue growing.
        </p>
        <Link
          to="/login"
          className="bg-[#F5D020] text-gray-900 font-semibold px-8 py-3 rounded-lg hover:bg-[#d4b31b] transition-colors"
        >
          Get Started for Free
        </Link>
      </section>

      <footer className="w-full border-t border-gray-200 px-8 py-6 text-center text-sm text-gray-400">
        © {new Date().getFullYear()} Pull. All rights reserved.
      </footer>
    </div>
  );
}

function AuthRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  if (isSignedIn) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

export function App() {
  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <QueryProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<AuthRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login/*" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <>
                  <SignedIn>
                      <Dashboard />
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            />
            <Route
              path="/products/new"
              element={
                <>
                  <SignedIn>
                    <NewProduct />
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            />
            <Route
              path="/integrations"
              element={
                <>
                  <SignedIn>
                    <Integrations />
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            />
            <Route
              path="/products/:id"
              element={
                <>
                  <SignedIn>
                    <Product />
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn />
                  </SignedOut>
                </>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryProvider>
    </ClerkProvider>
  );
}

export default App;
